/**
 * Instruction attachments API Service (KAN-702): материалы оператора, которые
 * бот и менеджер отправляют клиенту.
 *
 *   GET    /api/v1/instruction-attachments/?operator_id=<uuid>  - без operator_id: все видимые
 *   POST   /api/v1/instruction-attachments/        - multipart {file, title, description, operator_id, attach_to_email, is_main_price}
 *   PATCH  /api/v1/instruction-attachments/{id}/   - {title?, description?, enabled?, attach_to_email?, is_main_price?}
 *   DELETE /api/v1/instruction-attachments/{id}/
 *   POST   /api/v1/instruction-attachments/{id}/send/ - {client_id} -> {queued: true} | 400 {detail}
 *
 * KAN-717: attach_to_email - файл уходит вложением во вводном письме клиенту.
 * KAN-721: документы (PDF, Excel, Word) рядом с картинками, основной прайс-лист
 *   (один на оператора), ручная отправка материала клиенту из переписки.
 */
import apiClient from './client';

export type InstructionAttachmentKind = 'image' | 'document';

export interface InstructionAttachment {
  id: number;
  operator_id: string;
  operator_email: string;
  title: string;
  description: string;
  enabled: boolean;
  /** KAN-717: прикладывать к вводному письму клиенту. Нет на старом бэкенде. */
  attach_to_email?: boolean;
  file_url: string;
  created_at: string | null;
  /** KAN-721: имя файла при загрузке. Нет на старом бэкенде. */
  original_name?: string;
  /** KAN-721: картинка или документ. Нет на старом бэкенде (там только картинки). */
  kind?: InstructionAttachmentKind;
  /** KAN-721: размер файла в байтах. */
  size_bytes?: number;
  /** KAN-721: основной прайс-лист оператора (один на оператора). */
  is_main_price?: boolean;
}

export interface CreateInstructionAttachmentPayload {
  operatorId: string;
  file: File;
  title: string;
  description: string;
  attachToEmail: boolean;
  /** KAN-721: сразу отметить основным прайс-листом. */
  isMainPrice?: boolean;
}

export interface UpdateInstructionAttachmentPayload {
  title?: string;
  description?: string;
  enabled?: boolean;
  attach_to_email?: boolean;
  is_main_price?: boolean;
}

export interface SendInstructionAttachmentResponse {
  queued: boolean;
}

const BASE = '/api/v1/instruction-attachments/';

export const instructionAttachmentsApi = {
  /** Без operatorId - материалы всех операторов, видимых текущему пользователю. */
  list: async (operatorId?: string | null): Promise<InstructionAttachment[]> =>
    (await apiClient.get(BASE, operatorId ? { params: { operator_id: operatorId } } : undefined))
      .data,
  create: async ({
    operatorId,
    file,
    title,
    description,
    attachToEmail,
    isMainPrice = false,
  }: CreateInstructionAttachmentPayload): Promise<InstructionAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('description', description);
    formData.append('operator_id', operatorId);
    formData.append('attach_to_email', attachToEmail ? 'true' : 'false');
    formData.append('is_main_price', isMainPrice ? 'true' : 'false');
    return (
      await apiClient.post(BASE, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ).data;
  },
  update: async (
    id: number,
    data: UpdateInstructionAttachmentPayload
  ): Promise<InstructionAttachment> => (await apiClient.patch(`${BASE}${id}/`, data)).data,
  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`${BASE}${id}/`);
  },
  /** KAN-721: отправить материал клиенту в его переписку. */
  send: async (id: number, clientId: string): Promise<SendInstructionAttachmentResponse> =>
    (await apiClient.post(`${BASE}${id}/send/`, { client_id: clientId })).data,
};

export default instructionAttachmentsApi;
